import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMSStudentAssignments } from 'src/hooks/student-assignments';
import { useMSTeacherAssignments } from 'src/hooks/teacher-assignments';
import { useGetProductsByEntitlement } from 'src/hooks/use-get-products-by-entitlement.hook';
import { usePrevious } from 'src/hooks/use-previous.hook';
import { useTaughtLessons } from 'src/hooks/use-taught-lessons';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import authService from 'src/services/api/auth.service';
import {
  setIsNoAssignmentsDataViewMS,
  useActiveMSAssignmentsStatus,
  useActiveMSAssignmentsSubject,
  useIsNoAssignmentsDataViewMSSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { sortAssignments } from 'src/utils/assignments.utils';

const itemsCountPerOneScroll = 10;

// @TODO: remove after RFS
/**
 * A hook that returns an array of assignments that should be displayed to the user
 * in the Middle-School view.
 *
 * Initially returns 10 assignments and then 10 more each time,
 * when incrementItemsToDisplayCount function has been called.
 *
 * */
export const useMSAssignmentListData = () => {
  const dispatch = useAppDispatch();

  const isTeacher = authService.isTeacher();

  const selectedClass = useSelectedClassSelector();
  const selectedClassKey = selectedClass?.length
    ? selectedClass.map((cl) => cl.sourcedId)
    : [];
  const prevSelectedClassKey = usePrevious(selectedClassKey);
  const isNoAssignmentsDataViewMS = useIsNoAssignmentsDataViewMSSelector();
  const activeMSAssignmentsStatus = useActiveMSAssignmentsStatus();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();
  const { data: userAssets, isLoadingWithoutError: isAssetsLoading } =
    useUserAssets();
  const { productsByEntitlement } = useGetProductsByEntitlement();
  const [itemsToDisplayCount, setItemsToDisplayCount] = useState(
    itemsCountPerOneScroll,
  );

  const {
    data: studentAssignments,
    isLoading: isStudentAssignmentsLoading,
    isFetching: isStudentAssignmentsFetching,
  } = useMSStudentAssignments();
  const {
    data: teacherAssignments,
    isLoading: isTeacherAssignmentsLoading,
    isFetching: isTeacherAssignmentsFetching,
  } = useMSTeacherAssignments();
  const {
    data: taughtLessons,
    isLoading: isTaughtLessonsLoading,
    isFetching: isTaughtLessonsFetching,
  } = useTaughtLessons();

  const isAssignmentsFetching = isTeacher
    ? isTeacherAssignmentsFetching
    : isStudentAssignmentsFetching;
  const isFetching = isAssignmentsFetching || isTaughtLessonsFetching;

  const isAssignmentsLoading = isTeacher
    ? isTeacherAssignmentsLoading
    : isStudentAssignmentsLoading;
  const isLoading =
    isAssignmentsLoading || isTaughtLessonsLoading || isAssetsLoading;

  const assignmentsByRole = useMemo(() => {
    return isTeacher ? teacherAssignments : studentAssignments;
  }, [isTeacher, studentAssignments, teacherAssignments]);

  useEffect(() => {
    if (
      !isLoading &&
      !activeMSAssignmentsStatus &&
      !activeMSAssignmentsSubject &&
      assignmentsByRole
    ) {
      const taughtLessonsLength = taughtLessons?.length ?? 0;
      const assignmentsLength = assignmentsByRole.assignments.length;
      const allLessonsLength = assignmentsLength + taughtLessonsLength;

      dispatch(setIsNoAssignmentsDataViewMS(!allLessonsLength));
    }
  }, [
    productsByEntitlement.length,
    activeMSAssignmentsStatus,
    activeMSAssignmentsSubject,
    assignmentsByRole,
    dispatch,
    isLoading,
    userAssets,
    taughtLessons,
  ]);

  const itemsToDisplay = useMemo(() => {
    if (isNoAssignmentsDataViewMS) {
      return [];
    }

    const allDataItems = taughtLessons
      ? sortAssignments([
          ...(assignmentsByRole?.assignments ?? []),
          ...(!activeMSAssignmentsStatus ? taughtLessons : []),
        ])
      : assignmentsByRole?.assignments;

    return allDataItems?.slice(0, itemsToDisplayCount) ?? [];
  }, [
    itemsToDisplayCount,
    activeMSAssignmentsStatus,
    isNoAssignmentsDataViewMS,
    assignmentsByRole?.assignments,
    taughtLessons,
  ]);

  const incrementItemsToDisplayCount = useCallback(() => {
    setItemsToDisplayCount((prevState) => prevState + itemsCountPerOneScroll);
  }, []);

  // reset on selected class change
  useEffect(() => {
    const hasClassChanged =
      !!prevSelectedClassKey?.length &&
      selectedClassKey.join('') !== prevSelectedClassKey.join('');

    if (hasClassChanged) {
      setItemsToDisplayCount(itemsCountPerOneScroll);
    }
    // Avoid including prevSelectedClassKey in deps as we only track the active class update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassKey]);

  return {
    allAssignmentCount: assignmentsByRole?.assignments.length,
    itemsToDisplay,
    isLoading,
    isFetching,
    incrementItemsToDisplayCount,
  };
};
